import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    conversationalEditingService,
    ConversationMessage,
    ActionResult,
} from '../../services/conversationalEditingService';
import TranscriptPreview from './TranscriptPreview';
import ChatMessage from './ChatMessage';
import DiffPreview from './DiffPreview';
import CommandInput from './CommandInput';
import QuickActions from './QuickActions';
import './ConversationalEditor.css';

interface Sentence {
    text: string;
    speaker?: string | null;
    [key: string]: any;
}

interface ConversationalEditorProps {
    draftId: string;
    sentences: Sentence[];
    onSentencesChange: (sentences: Sentence[]) => void;
    onDone: () => void;
    audioUrl?: string | null;
    focusedIndex?: number | null;
    onPlaySentence?: (index: number) => void;
    onStopSentence?: () => void;
    playingSentenceIndex?: number | null;
}

const ConversationalEditor: React.FC<ConversationalEditorProps> = ({
    draftId,
    sentences,
    onSentencesChange,
    onDone,
    audioUrl,
    focusedIndex,
    onPlaySentence,
    onStopSentence,
    playingSentenceIndex,
}) => {
    const { t } = useTranslation(['conversationalEditor', 'common']);
    const [messages, setMessages] = useState<ConversationMessage[]>([]);
    const [pendingAction, setPendingAction] = useState<ActionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [affectedIndices, setAffectedIndices] = useState<number[]>([]);

    // Load history on mount
    useEffect(() => {
        const loadHistory = async () => {
            try {
                const history = await conversationalEditingService.getHistory(draftId);
                setMessages(history);
            } catch {
                // First time — no history
            }
        };
        loadHistory();
    }, [draftId]);

    const handleSend = useCallback(async (message: string) => {
        setLoading(true);
        setAffectedIndices([]);
        try {
            const response = await conversationalEditingService.sendMessage(draftId, message);
            setMessages(prev => [
                ...prev,
                // user message is saved server-side, but we optimistically show it
                { id: `temp-${Date.now()}`, draftId, role: 'user' as const, content: message, actionType: null, actionPayload: null, actionResult: null, sentencesSnapshot: null, createdAt: new Date().toISOString() },
                response.message,
            ]);

            if (response.requiresConfirmation && response.actionResult) {
                setPendingAction(response.actionResult);
                setAffectedIndices(response.actionResult.affectedIndices);
            }
        } catch {
            setMessages(prev => [
                ...prev,
                { id: `err-${Date.now()}`, draftId, role: 'assistant' as const, content: t('conversationalEditor:commandError'), actionType: null, actionPayload: null, actionResult: null, sentencesSnapshot: null, createdAt: new Date().toISOString() },
            ]);
        } finally {
            setLoading(false);
        }
    }, [draftId, t]);

    const handleConfirm = useCallback(async () => {
        setIsConfirming(true);
        try {
            const result = await conversationalEditingService.confirm(draftId);
            if (result.success && pendingAction) {
                onSentencesChange(pendingAction.proposedSentences);
                setMessages(prev => [
                    ...prev,
                    { id: `confirm-${Date.now()}`, draftId, role: 'assistant' as const, content: t('conversationalEditor:changesApplied', { count: result.sentenceCount }), actionType: 'confirm', actionPayload: null, actionResult: null, sentencesSnapshot: null, createdAt: new Date().toISOString() },
                ]);
            }
        } catch {
            // ignore
        } finally {
            setPendingAction(null);
            setAffectedIndices([]);
            setIsConfirming(false);
        }
    }, [draftId, pendingAction, onSentencesChange, t]);

    const handleCancel = useCallback(() => {
        setPendingAction(null);
        setAffectedIndices([]);
    }, []);

    const handleUndo = useCallback(async () => {
        setLoading(true);
        try {
            const result = await conversationalEditingService.undo(draftId);
            if (result.success) {
                if (result.sentences) {
                    onSentencesChange(result.sentences);
                }
                setMessages(prev => [
                    ...prev,
                    { id: `undo-${Date.now()}`, draftId, role: 'assistant' as const, content: t('conversationalEditor:undoComplete', { count: result.sentenceCount }), actionType: 'undo', actionPayload: null, actionResult: null, sentencesSnapshot: null, createdAt: new Date().toISOString() },
                ]);
            }
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, [draftId, t, onSentencesChange]);

    const handleRedo = useCallback(async () => {
        setLoading(true);
        try {
            const result = await conversationalEditingService.redo(draftId);
            if (result.success) {
                if (result.sentences) {
                    onSentencesChange(result.sentences);
                }
                setMessages(prev => [
                    ...prev,
                    { id: `redo-${Date.now()}`, draftId, role: 'assistant' as const, content: t('conversationalEditor:redoComplete', { count: result.sentenceCount }), actionType: 'redo', actionPayload: null, actionResult: null, sentencesSnapshot: null, createdAt: new Date().toISOString() },
                ]);
            }
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, [draftId, t, onSentencesChange]);

    return (
        <div className="ce-editor">
            <div className="ce-editor__transcript">
                <h3 className="ce-editor__section-title">
                    {t('conversationalEditor:transcriptTitle')} ({sentences.length})
                </h3>
                <TranscriptPreview
                    sentences={sentences}
                    affectedIndices={affectedIndices}
                    audioUrl={audioUrl}
                    focusedIndex={focusedIndex}
                    onPlaySentence={onPlaySentence}
                    onStopSentence={onStopSentence}
                    externalPlayingIndex={playingSentenceIndex}
                />
            </div>

            <div className="ce-editor__chat">
                <h3 className="ce-editor__section-title">{t('conversationalEditor:chatTitle')}</h3>
                <div className="ce-editor__messages">
                    {messages.length === 0 && (
                        <div className="ce-editor__welcome">
                            {t('conversationalEditor:welcomeMessage', { count: sentences.length })}
                        </div>
                    )}
                    {messages.filter(m => m.role !== 'system').map((msg) => (
                        <ChatMessage key={msg.id} role={msg.role as 'user' | 'assistant'} content={msg.content} />
                    ))}
                    {loading && (
                        <div className="ce-editor__typing">
                            <span /><span /><span />
                        </div>
                    )}
                </div>

                {pendingAction && (
                    <DiffPreview
                        preview={pendingAction.preview}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                        isConfirming={isConfirming}
                    />
                )}

                <CommandInput onSend={handleSend} disabled={loading || !!pendingAction} />

                <QuickActions
                    onAction={handleSend}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    onDone={onDone}
                    disabled={loading || !!pendingAction}
                />
            </div>
        </div>
    );
};

export default ConversationalEditor;
